import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { io } from "socket.io-client";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppDataProvider } from "./contexts/AppDataContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SwiftAlert from "./components/SwiftAlert";
import { API_URL } from "./services/api";
import { queryCache } from "./lib/queryCache";

// Import Pages
import LandingPage from "./pages/User/LandingPage";
import Register from "./pages/User/Register";
import Login from "./pages/User/Login";
import ForgotPassword from "./pages/User/ForgotPassword"; // ✅ Added Import
import Dashboard from "./pages/User/Dashboard";
import CreateReviewer from "./pages/User/CreateReviewer";
import BoardHub from "./pages/User/BoardHub";
import Library from "./pages/User/Library";
import Analytics from "./pages/User/Analytics";
import Profile from "./pages/User/Profile";
import StudyMode from "./pages/User/StudyMode";
import FolderView from "./pages/User/FolderView";
import Achievements from "./pages/User/Achievements";
import ArchivePage from "./pages/User/ArchivePage";
import ArchiveFolderView from "./pages/User/ArchiveFolderView";
import StudentMadeExam from "./pages/User/StudentMadeExam";
import StudyGroup from "./pages/User/StudyGroup";
import StudyGroupHub from "./pages/User/StudyGroupHub";
import TermsOfService from "./pages/User/TermsOfService";
import PrivacyPolicy from "./pages/User/PrivacyPolicy";

const DirectShareNotifications = () => {
  const { user, loading } = useAuth();
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (loading || !user) return undefined;

    const token = localStorage.getItem("jwt_token");
    const socket = io(API_URL.replace(/\/api$/, ""), {
      withCredentials: true,
      path: "/api/socket.io",
      auth: token ? { token } : undefined,
    });

    if (user?._id || user?.id) {
      socket.emit("join_user", String(user._id || user.id));
    }

    const updateGroupsCache = (groupId, count) => {
      if (!groupId) return;
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached)) {
        const next = cached.map((g) =>
          String(g.id) === String(groupId)
            ? { ...g, pendingRequestsCount: Number(count || 0) }
            : g
        );
        queryCache.set("study_groups_mapped", next, 5 * 60 * 1000);
      }
    };

    socket.on("reviewer_shared", ({ reviewer, senderName }) => {
      setAlert({
        type: "success",
        title: "Reviewer Shared With You",
        message: `${senderName || "A user"} shared "${reviewer?.topic || "a reviewer"}" with you.`,
      });
    });

    socket.on("study_group_accepted", ({ group, groupName, message }) => {
      queryCache.invalidate("study_groups_mapped");
      queryCache.invalidate("study_groups_list");
      window.dispatchEvent(new CustomEvent("study_group_updated", { detail: { action: "accepted", group } }));
      setAlert({
        type: "success",
        title: "Study Group Accepted!",
        message: message || `Your request to join "${group?.name || groupName || "the group"}" was accepted!`,
      });
    });

    socket.on("study_group_declined", ({ groupName, message }) => {
      setAlert({
        type: "info",
        title: "Join Request Declined",
        message: message || `Your request to join "${groupName || "the group"}" was declined.`,
      });
    });

    socket.on("study_group_removed", ({ groupId, groupName, message }) => {
      queryCache.invalidate("study_groups_mapped");
      queryCache.invalidate("study_groups_list");
      window.dispatchEvent(new CustomEvent("study_group_updated", { detail: { action: "removed", groupId } }));
      setAlert({
        type: "warning",
        title: "Removed from Study Group",
        message: message || `You have been removed from "${groupName || "the group"}".`,
      });
    });

    socket.on("study_group_request_received", ({ groupId, groupName, pendingRequestsCount, totalPendingRequests, message }) => {
      updateGroupsCache(groupId, pendingRequestsCount);
      window.dispatchEvent(
        new CustomEvent("study_group_requests_changed", {
          detail: {
            groupId: String(groupId),
            pendingRequestsCount: Number(pendingRequestsCount || 0),
            totalPendingRequests: typeof totalPendingRequests === "number" ? totalPendingRequests : undefined,
          },
        })
      );
      setAlert({
        type: "info",
        title: "New Join Request",
        message: message || `A new student requested to join "${groupName || "your group"}".`,
      });
    });

    socket.on("study_group_requests_changed", ({ groupId, pendingRequestsCount, totalPendingRequests }) => {
      updateGroupsCache(groupId, pendingRequestsCount);
      window.dispatchEvent(
        new CustomEvent("study_group_requests_changed", {
          detail: {
            groupId: String(groupId),
            pendingRequestsCount: Number(pendingRequestsCount || 0),
            totalPendingRequests: typeof totalPendingRequests === "number" ? totalPendingRequests : undefined,
          },
        })
      );
    });

    const updateUnreadMessageCache = (groupId, increment = true) => {
      if (!groupId) return;
      const cached = queryCache.get("study_groups_mapped");
      if (Array.isArray(cached)) {
        const next = cached.map((g) => {
          if (String(g.id) === String(groupId)) {
            const nextCount = increment ? Number(g.unreadMessagesCount || 0) + 1 : 0;
            return {
              ...g,
              unreadMessagesCount: nextCount,
              hasUnreadMessages: nextCount > 0,
            };
          }
          return g;
        });
        queryCache.set("study_groups_mapped", next, 5 * 60 * 1000);
      }
    };

    socket.on("study_group_message_received", ({ groupId, groupName, message }) => {
      updateUnreadMessageCache(groupId, true);
      window.dispatchEvent(
        new CustomEvent("study_group_message_received", {
          detail: { groupId: String(groupId), groupName, message },
        })
      );
    });

    socket.on("study_group_chat_read", ({ groupId }) => {
      updateUnreadMessageCache(groupId, false);
      window.dispatchEvent(
        new CustomEvent("study_group_chat_read", {
          detail: { groupId: String(groupId) },
        })
      );
    });

    return () => socket.disconnect();
  }, [loading, user]);

  return alert ? <SwiftAlert {...alert} onClose={() => setAlert(null)} /> : null;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppDataProvider>
          <DirectShareNotifications />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} /> {/* ✅ Added Route */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/create-reviewer" element={<CreateReviewer />} />
              <Route path="/board-prep" element={<BoardHub />} />

              <Route path="/study-group" element={<StudyGroup />} />
              <Route path="/study-group/:id/hub" element={<StudyGroupHub />} />
              <Route path="/board-prep/view" element={<StudentMadeExam />} />

              <Route path="/library" element={<Library />} />
              <Route path="/library/archive" element={<ArchivePage />} />
              <Route path="/library/archive/:folderId" element={<ArchiveFolderView />} />
              <Route path="/library/:id" element={<FolderView />} />

              <Route path="/achievements" element={<Achievements />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/profile" element={<Profile />} />

              <Route path="/study-mode/:id" element={<StudyMode />} />
              <Route path="/study-mode" element={<StudyMode />} />
            </Route>
          </Routes>
        </AppDataProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
